import { Select } from "@mantine/core";
import { DateTime } from "luxon";
import { Frequency } from "../../utils/enums";
import "./MonthYearRangePicker.scss";

export interface MonthYearRangePickerProps {
  start: DateTime<true>;
  end: DateTime<true>;
  onChange: (dates: {
    startDate: DateTime<true>;
    endDate: DateTime<true> | null;
  }) => void;
  mode?: Frequency.Month | Frequency.Year;
  monthRange?: number;
  yearRange?: number;
}

interface MonthYearRangePickerOption {
  value: string;
  label: string;
  date: DateTime<true>;
}

function MonthYearRangePicker({
  start,
  end,
  onChange,
  mode = Frequency.Year,
  monthRange = 4 * 12,
  yearRange = 4
}: MonthYearRangePickerProps) {
  const startOptions = buildOptions(DateTime.local().startOf(mode));
  const endOptions = buildOptions(DateTime.local().endOf(mode));

  function buildOptions(
    pivotDate: DateTime<true>
  ): Array<MonthYearRangePickerOption> {
    const pastOptions: Array<MonthYearRangePickerOption> = [];
    const range = mode === "month" ? monthRange : yearRange;

    for (let i = 1; i <= range; i++) {
      const pastDate = pivotDate.minus({ [mode]: i });
      pastOptions.push(dateToOption(pastDate));
    }

    pastOptions.reverse();

    return [...pastOptions, dateToOption(pivotDate)];
  }

  function dateToOption(date: DateTime<true>): MonthYearRangePickerOption {
    let format: Intl.DateTimeFormatOptions;

    if (mode === "month") {
      format = {
        month: "long",
        year: "numeric"
      };
    } else {
      format = {
        year: "numeric"
      };
    }

    return {
      value: date.toFormat(mode === "month" ? "MM yyyy" : "yyyy"),
      label: date.setLocale("de-DE").toLocaleString(format),
      date
    };
  }

  function handleStartChange(newStart: string | null) {
    const selected = startOptions.find((option) => option.value === newStart);

    if (!selected) {
      return;
    }

    const newStartDateTime = selected.date.startOf(mode);
    const sanitizedEnd =
      newStartDateTime > end ? newStartDateTime.endOf(mode) : end;
    onChange({ startDate: newStartDateTime, endDate: sanitizedEnd });
  }

  function handleEndChange(newEnd: string | null) {
    const selected = endOptions.find((option) => option.value === newEnd);

    if (!selected) {
      onChange({ startDate: start, endDate: null });
      return;
    }

    const newEndDateTime = selected.date.endOf(mode);
    const sanitizedStart =
      newEndDateTime < start ? newEndDateTime.startOf(mode) : start;
    onChange({
      startDate: sanitizedStart,
      endDate: newEndDateTime
    });
  }

  return (
    <div className="FormField">
      <div className="MonthYearRangePicker">
        <Select
          data={startOptions}
          name="start"
          value={dateToOption(start).value}
          onChange={handleStartChange}
        />
        <span className="divider">bis</span>
        <Select
          data={endOptions}
          name="end"
          value={dateToOption(end).value}
          onChange={handleEndChange}
        />
      </div>
    </div>
  );
}

export { MonthYearRangePicker };
